/// <amd-module name="Build/Designer/YamlEditor" />
import { CodeLensOptions, ILanguageEditorService } from "Build/Common/Library/Contracts";
import * as Resources from "Build/Designer/Resources";
import * as React from "react";
import { IEditorProps } from "VSS/Features/Monaco/Contracts";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface Props {
    content: string;
    errorMessage: string;
    isReadOnly: boolean;
    /** Selector of the element that should take focus when Escape is pressed while editing */
    elementToFocusOnEscape?: string;
    spinnerMessage?: string;
    onEditorCreated: (editor: monaco.editor.IEditor) => void;
    onEditorContentUpdated: (editor: monaco.editor.IEditor) => void;
    onEditorDisposing?: (editor: monaco.editor.IEditor) => void;

    sidePanelIsOpen: boolean;
    codeLensOptions?: CodeLensOptions;
}

interface State {
    isEditorLoaded: boolean;
    monacoTheme: monaco.editor.BuiltinTheme;
}

export class YamlEditor extends VssComponent<Props, State, IVssComponentContext> {
    private _editor?: monaco.editor.IEditor;
    private _disposables: monaco.IDisposable[] = [];

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            isEditorLoaded: false,
            monacoTheme: this._getEditorTheme()
        };
    }

    public render(): JSX.Element {
        return (
            <>
                {this.props.spinnerMessage ? (
                    this._getSpinner(this.props.spinnerMessage)
                ) : (
                    <>
                        <CodeEditorWrapper
                            content={this.props.content}
                            errorMessage={this.props.errorMessage}
                            isReadOnly={this.props.isReadOnly}
                            elementToFocusOnEscape={this.props.elementToFocusOnEscape}
                            monacoTheme={this.state.monacoTheme}
                            onEditorContentUpdated={this.props.onEditorContentUpdated}
                            onEditorCreated={this._onEditorCreated}
                        />

                        {!this.state.isEditorLoaded && this._getSpinner(Resources.LoadingEditor)}
                    </>
                )}
            </>
        );
    }

    public componentDidMount(): void {
        window.addEventListener("resize", this._onResizeHandler);
        this._themeService.subscribe(this._onThemeChanged, "themeChanged");
    }

    public componentWillUnmount(): void {
        window.removeEventListener("resize", this._onResizeHandler);
        this._themeService.unsubscribe(this._onThemeChanged, "themeChanged");

        if (this._editor) {
            if (this.props.onEditorDisposing) {
                this.props.onEditorDisposing(this._editor);
            }
            this._disposables.forEach(d => d.dispose());
            this._editor = undefined;
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: IVssComponentContext): void {
        super.componentDidUpdate(prevProps, prevState, snapshot);

        // Hide the Monaco minimap when the Tasks panel is open to reduce clutter
        if (this._editor && this.props.sidePanelIsOpen !== prevProps.sidePanelIsOpen) {
            this._editor.updateOptions({ minimap: { enabled: !this.props.sidePanelIsOpen } });
            this._editor.layout();
        }
    }

    private _getSpinner(label: string): JSX.Element {
        return <Spinner className="absolute-fill" size={SpinnerSize.large} label={label} />;
    }

    private _onEditorCreated = (editor: monaco.editor.IEditor) => {
        if (editor) {
            this._editor = editor;
            this._disposables.push(this._editor);

            // Enter key should trigger the suggestion widget as long as no widgets are
            // currently showing.
            const codeEditor = this._editor as monaco.editor.IStandaloneCodeEditor;
            codeEditor.addCommand(
                monaco.KeyCode.Enter,
                () => {
                    editor.trigger("keyboard", "type", { text: "\n" });
                    editor.trigger("", "editor.action.triggerSuggest", {});
                },
                "!suggestWidgetVisible && !markersNavigationVisible && !parameterHintsVisible && !findWidgetVisible"
            );

            if (this.props.onEditorCreated) {
                this.props.onEditorCreated(editor);
            }

            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getService<ILanguageEditorService>("ms.vss-build-web.language-editor-service").then(service => {
                service!.initializeEditor(codeEditor, this.props.codeLensOptions);
                codeEditor.layout();
            });

            this.setState({ isEditorLoaded: true });
        }
    };

    private _onResizeHandler = () => {
        if (this._editor) {
            this._editor.layout();
        }
    };

    private _onThemeChanged = () => {
        const monacoTheme = this._getEditorTheme();
        this.setState({ monacoTheme });
    };

    private _getEditorTheme(): monaco.editor.BuiltinTheme {
        const currentTheme = this._themeService.getCurrentTheme();
        return currentTheme && currentTheme.isDark ? "vs-dark" : "vs";
    }

    private get _themeService() {
        return this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
    }
}

interface CodeEditorWrapperProps {
    content: string;
    errorMessage: string;
    monacoTheme: monaco.editor.BuiltinTheme;
    isReadOnly: boolean;
    elementToFocusOnEscape?: string;
    onEditorCreated(editor: monaco.editor.IEditor): void;
    onEditorContentUpdated(editor: any): void;
}

class CodeEditorWrapper extends React.PureComponent<CodeEditorWrapperProps> {
    private _editorOptions: monaco.editor.IEditorOptions;

    constructor(props: CodeEditorWrapperProps) {
        super(props);

        this._editorOptions = {
            fontSize: 14, // $fontSizeM
            lineNumbersMinChars: 8, // Provides a left margin with full width scroll shadow at top
            readOnly: this.props.isReadOnly,
            renderWhitespace: "all",
            fixedOverflowWidgets: true,
            quickSuggestions: {
                comments: false,
                strings: true,
                other: true
            },
            quickSuggestionsDelay: 200,
            suggestOnTriggerCharacters: true,
            ...{ wordBasedSuggestions: false },
            minimap: {
                enabled: false
            }
        };
    }

    public render(): JSX.Element {
        const options: monaco.editor.IEditorOptions = {
            ...this._editorOptions,
            readOnly: this.props.isReadOnly
        };

        return (
            <WrappedComponent<IEditorProps>
                dependencies={["ms.vss-features.editor"]}
                wrappedType="code-editor"
                wrappedProps={{
                    className: "ci-editor-host" + (this.props.errorMessage ? " ci-show-error" : ""),
                    monacoTheme: this.props.monacoTheme,
                    editorOptions: options,
                    elementToFocusOnEscape: this.props.elementToFocusOnEscape,
                    editorRef: this.props.onEditorCreated,
                    onContentUpdated: this.props.onEditorContentUpdated,
                    content: {
                        content: this.props.content,
                        contentType: "application/x-yaml"
                    }
                }}
            />
        );
    }
}
